import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import Spinner from "../../components/ui/spinner";
import { Lock, AlertCircle } from "lucide-react";
import { useVerifyPrototypeMutation } from "../../data/mutations/prototypes";
import { useParams } from "react-router-dom";
import { TekkrIcon } from "../../components/shared/tekkr/tekkr-logo";

export default function ViewerPage() {
    const { slug } = useParams<{ slug: string }>();
    const [htmlContent, setHtmlContent] = useState<string | null>(null);
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const verify = useVerifyPrototypeMutation();

    if (!slug) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <p>Invalid prototype URL</p>
            </div>
        );
    }

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (!password) return;

        try {
            const result = await verify.mutateAsync({ slug, password });
            if (result.valid && result.html) {
                setHtmlContent(result.html);
            } else {
                setError("Incorrect password. Please try again.");
            }
        } catch {
            setError("An error occurred. Please try again.");
        }
    };

    if (htmlContent) {
        return (
            <div className="flex h-screen flex-col">
                <div className="flex items-center border-b bg-background px-4 py-3">
                    <TekkrIcon className="h-8 w-8" />
                </div>
                <div className="flex-1">
                    <HtmlViewer htmlContent={htmlContent} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-muted/50 p-4">
            <Card className="w-full max-w-md">
                <CardContent className="pt-6 text-center">
                    <TekkrIcon className="mx-auto mb-4 h-12 w-12" />

                    <h2 className="mb-1 text-xl font-semibold">Prototype</h2>
                    <p className="mb-6 text-sm text-muted-foreground">Enter the password to view this prototype</p>

                    <form onSubmit={handleVerify} className="space-y-4 text-left">
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                placeholder="Enter access password"
                                className={error ? "border-destructive" : ""}
                            />
                            {error && (
                                <div className="flex items-center gap-1 text-sm text-destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    {error}
                                </div>
                            )}
                        </div>

                        <Button type="submit" className="w-full" disabled={verify.isPending || !password}>
                            {verify.isPending ? (
                                <>
                                    <Spinner className="mr-2 h-4 w-4" /> Verifying...
                                </>
                            ) : (
                                <>
                                    <Lock className="mr-2 h-4 w-4" /> View prototype
                                </>
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

function HtmlViewer({ htmlContent }: { htmlContent: string }) {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);

    useEffect(() => {
        const blob = new Blob([htmlContent], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [htmlContent]);

    if (!blobUrl) return null;

    return (
        <iframe
            src={blobUrl}
            sandbox="allow-scripts allow-popups"
            className="h-full w-full border-0"
            title="Prototype Preview"
        />
    );
}
